import { CopyIcon } from "@radix-ui/react-icons";
import { Alert01Icon } from "hugeicons-react";
import { useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ThreeDots } from "react-loader-spinner";
import { checkTagValidity } from "../../Utils/Utils";
import {
  RECUPERER_ZONE_COVERTURE_FICHIER,
  VERIFICATION_ELIGIBILITE,
} from "../../Utils/constant";
import { find, store } from "../../services/service";
import Kml from "../Widget/Kml";
import { SubmitButton } from "../Widget/SubmitButton";

export const Eligibilite = ({ handleNext }) => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [error, setError] = useState(null);
  const [loadingLocalisation, setLoadingLocalisation] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [gettingFile, setGettingFile] = useState(false);
  const [fileZoneCouverte, setFileZoneCouverte] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [debouncedFieldLatitude, setDebouncedFieldLatitude] =
    useState(latitude);
  const [debouncedFieldLongitude, setDebouncedFieldLongitude] =
    useState(longitude);
  const [msg, setMsg] = useState(null);
  // États pour la recherche d'adresse
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const searchTimeoutRef = useRef(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);

  useEffect(() => {
    setGettingFile(true);
    find(RECUPERER_ZONE_COVERTURE_FICHIER)
      .then((res) => {
        setGettingFile(false);
        setFileZoneCouverte(
          res.data && res.data.fichier ? res.data.fichier : null,
        );
      })
      .catch((err) => {
        setGettingFile(false);
      });
  }, []);

  const handleClick = () => {
    if ("geolocation" in navigator) {
      setLoadingLocalisation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLoadingLocalisation(false);
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setLatitude(lat);
          setLongitude(lon);
          setUserLocation([lat, lon]);
          setError(null);

          setMapCenter([lat, lon]);
          setMapZoom(14);
        },
        (error) => {
          setLoadingLocalisation(false);
          setError(error.message);
        },
        {
          timeout: 10000,
          maximumAge: 0,
          enableHighAccuracy: true,
        },
      );
    } else {
      setLoadingLocalisation(false);
      setError(
        "La géolocalisation n'est pas prise en charge par ce navigateur.",
      );
      console.error("Geolocation not supported by this browser.");
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [isSubmit, setIsSubmit] = useState(false);

  useEffect(() => {
    if (userLocation) {
      reset({
        latitude: latitude,
        longitude: longitude,
      });
    }
  }, [reset, userLocation]);

  const submit = (data) => {
    setIsSubmit(true);

    store(VERIFICATION_ELIGIBILITE, "application/json", {
      ...data,
    })
      .then((res) => {
        if (res.status === 200) {
          setEligible(res?.data);
          localStorage.setItem("numero", res?.data?.numeroTicket);
          localStorage.setItem("eligible", res?.data?.fttheligible);
          toast.success("Vérification terminée");
          setIsSubmit(false);
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        setMsg(err?.response?.data?.donnee);
      });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFieldLatitude(latitude);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [latitude]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFieldLongitude(longitude);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [longitude]);

  useEffect(() => {
    if (debouncedFieldLatitude || debouncedFieldLongitude) {
      if (
        latitude &&
        latitude.length >= 3 &&
        longitude &&
        longitude.length > 3
      ) {
        setUserLocation([latitude, longitude]);
      }
    }
  }, [debouncedFieldLatitude, debouncedFieldLongitude, latitude, longitude]);

  // Fonction de recherche d'adresse avec l'API Nominatim
  const searchAddress = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query,
        )}&limit=5&addressdetails=1&countrycodes=ci`,
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresse:", error);
      toast.error("Erreur lors de la recherche d'adresse");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 500);
  };

  const handleAddressSelect = (address) => {
    const lat = parseFloat(address.lat);
    const lon = parseFloat(address.lon);

    setSelectedAddress(address);
    setLatitude(lat.toString());
    setLongitude(lon.toString());
    setUserLocation([lat, lon]);
    setSearchTerm(address.display_name);
    setSearchResults([]);
    setMapCenter([lat, lon]);
    setMapZoom(13);

    toast.success("Adresse sélectionnée");
  };

  const clearAddressSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedAddress(null);
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            Vérification d'éligibilité FTTH
          </h1>
          <p className="text-gray-600 text-xs">
            Vérifiez si votre zone est couverte par notre réseau fibre optique
          </p>

          {msg && (
            <p className="text-red-600 text-xs flex gap-2">
              <Alert01Icon /> {msg}
            </p>
          )}
        </div>

        {/* Disposition principale */}
        <div className="flex flex-col xl:flex-row gap-2">
          {/* Section Carte - Pleine largeur sur mobile, 60% sur desktop */}
          <div className="w-full xl:w-3/5">
            <div className="bg-white rounded-xl shadow p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">
                  Zones de couverture FTTH
                </h2>
                <div className="hidden md:flex items-center space-x-2 text-sm text-gray-500">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Zone couverte</span>
                </div>
              </div>

              <div className="relative">
                {!gettingFile ? (
                  <div className="w-full overflow-hidden rounded-lg">
                    <Kml
                      userLocation={userLocation}
                      setLatitude={setLatitude}
                      setLongitude={setLongitude}
                      setUserLocation={setUserLocation}
                      mapCenter={mapCenter}
                      mapZoom={mapZoom}
                      zoneCouverte={fileZoneCouverte}
                      onMapCenterChange={setMapCenter}
                      onMapZoomChange={setMapZoom}
                    />
                  </div>
                ) : (
                  <div className="w-full h-[200px] lg:h-[400px]  flex items-center justify-center bg-gray-100 rounded-lg border-2 border-gray-200">
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                      <p className="mt-2 text-gray-600 text-xs">
                        Chargement de la carte...
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Légende mobile */}
              <div className="md:hidden mt-4 flex items-center justify-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Zone couverte</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section Formulaire - Pleine largeur sur mobile, 40% sur desktop */}
          <div className="w-full xl:w-2/5">
            <div className="bg-white rounded-xl shadow p-4 md:p-6">
              <h2 className="text-sm md:text-xl font-semibold text-gray-900 mb-6">
                Informations de localisation
              </h2>

              <form onSubmit={handleSubmit(submit)} className="space-y-6">
                {/* Recherche d'adresse */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Rechercher une adresse
                  </label>
                  <div className="relative">
                    <input
                      disabled={isSubmit || gettingFile}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      type="text"
                      placeholder="Ex: Cocody, Abidjan, Côte d'Ivoire"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={clearAddressSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Indicateur de recherche */}
                  {isSearching && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-center space-x-2">
                        <ThreeDots
                          height="20"
                          width="40"
                          radius="9"
                          color="#3B82F6"
                        />
                        <span className="text-sm text-blue-600 font-medium">
                          Recherche en cours...
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Résultats de recherche */}
                  {searchResults.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg max-h-48 overflow-y-auto shadow-sm">
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleAddressSelect(result)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors duration-200"
                        >
                          <div className="font-medium text-gray-900 truncate">
                            {result.name || result.display_name.split(",")[0]}
                          </div>
                          <div className="text-sm text-gray-500 truncate mt-1">
                            {result.display_name}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Adresse sélectionnée */}
                  {selectedAddress && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <svg
                          className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-green-800">
                            Adresse sélectionnée :
                          </p>
                          <p className="text-sm text-green-700 mt-1 break-words">
                            {selectedAddress.display_name}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {eligible === null ? (
                  <div className="space-y-4">
                    {/* Numéro client */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Numéro client <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("numeroClient", { required: true })}
                        type="number"
                        min={0}
                        placeholder="Entrez votre numéro client"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      {errors.numeroClient && (
                        <p className="mt-1 text-sm text-red-600">
                          Ce champ est requis
                        </p>
                      )}
                    </div>

                    {/* Coordonnées */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Latitude <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register("latitude", { required: true })}
                          type="text"
                          value={latitude || ""}
                          onChange={(e) => setLatitude(e.target.value)}
                          placeholder="Latitude"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                        />
                        {errors.latitude && (
                          <p className="mt-1 text-sm text-red-600">
                            Ce champ est requis
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Longitude <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register("longitude", { required: true })}
                          type="text"
                          value={longitude || ""}
                          onChange={(e) => setLongitude(e.target.value)}
                          placeholder="Longitude"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                        />
                        {errors.longitude && (
                          <p className="mt-1 text-sm text-red-600">
                            Ce champ est requis
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bouton géolocalisation */}
                    <button
                      type="button"
                      onClick={handleClick}
                      disabled={loadingLocalisation}
                      className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium text-sm flex items-center justify-center space-x-2"
                    >
                      {loadingLocalisation ? (
                        <>
                          <ThreeDots
                            height="20"
                            width="40"
                            radius="9"
                            color="#ffffff"
                          />
                          <span>Localisation en cours...</span>
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span>
                            {latitude && longitude
                              ? "Actualiser la position"
                              : "Obtenir ma position"}
                          </span>
                        </>
                      )}
                    </button>

                    {/* Bouton de vérification */}
                    <SubmitButton
                      isSubmit={isSubmit}
                      label="Vérifier l'éligibilité"
                    />
                  </div>
                ) : (
                  /* Résultat de l'éligibilité */
                  <div
                    className={`border-2 rounded-xl p-6 ${
                      eligible?.fttheligible !== "eligible"
                        ? "border-red-300 bg-red-50"
                        : "border-green-300 bg-green-50"
                    }`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            eligible?.fttheligible !== "eligible"
                              ? "bg-red-500"
                              : "bg-green-500"
                          }`}
                        >
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {eligible?.fttheligible !== "eligible" ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            )}
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Statut d'éligibilité
                          </p>
                          <p
                            className={`text-lg font-bold ${
                              eligible?.fttheligible !== "eligible"
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {eligible?.fttheligible}
                          </p>
                        </div>
                      </div>

                      <div className="bg-white bg-opacity-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700">
                            Numéro de ticket
                          </p>
                          <CopyToClipboard
                            text={eligible?.numeroTicket}
                            onCopy={() => toast.success("Numéro copié !")}
                          >
                            <span className="cursor-pointer p-2 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-200 shadow-sm">
                              <CopyIcon className="w-4 h-4 text-gray-600" />
                            </span>
                          </CopyToClipboard>
                        </div>
                        <p
                          className={`text-lg font-mono ${
                            eligible?.fttheligible !== "eligible"
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {eligible?.numeroTicket}
                        </p>
                      </div>

                      {eligible?.raison && (
                        <div className="bg-white bg-opacity-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Détails
                          </p>
                          <p className="text-sm text-gray-600">
                            {eligible.raison}
                          </p>
                        </div>
                      )}

                      {eligible?.distancePbo !== null && (
                        <div className="bg-white bg-opacity-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Distance PBO
                          </p>
                          <p className="text-sm text-gray-600">
                            {eligible.distancePbo} mètres
                          </p>
                        </div>
                      )}

                      {eligible?.fttheligible !== "eligible" && (
                        <button
                          type="button"
                          onClick={() => setEligible(null)}
                          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                        >
                          Réessayer
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-center sm:text-left">
              <p className="text-sm text-gray-600">
                {eligible?.fttheligible === "Non éligible"
                  ? "Votre zone n'est pas encore couverte"
                  : eligible?.fttheligible === "eligible"
                    ? "Félicitations ! Votre zone est couverte"
                    : "Vérifiez votre éligibilité pour continuer"}
              </p>
            </div>
            <div className="flex space-x-3">
              {eligible?.fttheligible === "Non éligible" ? (
                <button
                  onClick={() => window.history.back()}
                  className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
                >
                  Retour
                </button>
              ) : eligible?.fttheligible === "eligible" &&
                checkTagValidity("AJOUTER_INTENTION") ? (
                <button
                  onClick={handleNext}
                  className="bg-main text-black py-2 px-6 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-medium"
                >
                  Poursuivre →
                </button>
              ) : (
                <button
                  onClick={() => window.history.back()}
                  className="bg-main text-black py-2 px-6 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-medium"
                >
                  Retour
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
